import Cookie from "js-cookie";
import { omit } from "lodash";

import * as Events from "@app/analytics/tracking/events";
import { CALENDAR_PROVIDERS_BY_ID } from "@app/common/constants/providers";
import useApps from "@app/common/hooks/useApps";
import ROUTES from "@app/common/routes";
import { MeetingPlatform } from "@app/common/types";
import type { CalendarProviderId } from "@app/common/types/providerTypes";
import Config from "@app/config";
import { configureZoomSdk } from "@app/embedded/utils";
import openAppBrowser from "@app/embedded/zoom/utils/openAppBrowser";
import logger from "@app/utils/logger";

export interface ConnectCalendarOptions {
  // override the implicit client
  client?: MeetingPlatform;
  returnTo?: string;
  errorReturnTo?: string;
  action?: string;
  errorAction?: string;
  queryParams?: { [index: string]: string | number | boolean };
}

const getAppUserId = () => encodeURIComponent(Cookie.get("user_cookie") || "");

const getCalendarReturnUrl = (client: string, opts: ConnectCalendarOptions) => {
  const { queryParams = {}, action, ...otherOpts } = opts;
  let url;
  let clientParams: { [key: string]: string | boolean } = {
    ...queryParams,
    client,
    success: true,
  };

  if (client === "zoom") {
    url = `${Config.api.HOST}/zoomapp/deeplink`;
    clientParams = {
      ...clientParams,
      action: action ?? "calendarSuccess",
      encrypted_zoom_user_id: getAppUserId(),
    };
  } else if (client === "webex") {
    url = `${window.location.protocol}//${window.location.hostname}/welcome/finish`;
  } else if (opts.returnTo) {
    if (opts.returnTo.startsWith("http")) {
      url = opts.returnTo;
    } else {
      url = `${window.location.protocol}//${window.location.hostname}${opts.returnTo}`;
    }
    clientParams = {
      ...clientParams,
      action: action ?? "calendarSuccess",
    };
  } else {
    url = `${window.location.protocol}//${window.location.hostname}${ROUTES.USER_SETTINGS_INTEGRATIONS}`;
    clientParams = {
      ...clientParams,
      action: "connect",
    };
  }

  url = `${url}?${new URLSearchParams({
    ...omit(otherOpts, "returnTo"),
    ...clientParams,
  }).toString()}`;
  return encodeURIComponent(url);
};

const getCalendarErrorUrl = (client: string, opts: ConnectCalendarOptions) => {
  const { queryParams = {}, action: _action, errorAction, ...otherOpts } = opts;
  let url;
  let clientParams: { [key: string]: string | boolean } = {
    ...queryParams,
    client,
    error: true,
  };

  if (client === "zoom") {
    url = `${Config.api.HOST}/zoomapp/deeplink`;
    clientParams = {
      ...clientParams,
      action: errorAction ?? "calendarError",
      encrypted_zoom_user_id: getAppUserId(),
    };
  } else if (client === "webex") {
    url = `${window.location.protocol}//${window.location.hostname}/welcome/calendar`;
  } else {
    const returnLocation =
      opts.errorReturnTo ?? opts.returnTo ?? ROUTES.MEETING_REPORTS;
    if (returnLocation.startsWith("http")) {
      url = returnLocation;
    } else {
      url = `${window.location.protocol}//${window.location.hostname}${returnLocation}`;
    }
    clientParams = {
      ...clientParams,
      ...(errorAction ? { action: errorAction } : {}),
      returnTo: returnLocation,
    };
  }

  url = `${url}?${new URLSearchParams({
    ...otherOpts,
    ...clientParams,
  }).toString()}`;
  return encodeURIComponent(url);
};

export const getCalendarConnectUrl = ({
  client,
  calendarProvider,
  opts = {},
}: {
  client: string;
  calendarProvider: CalendarProviderId;
  opts?: ConnectCalendarOptions;
}) => {
  const baseUrl = CALENDAR_PROVIDERS_BY_ID[calendarProvider].connectUrl;
  let url = `${baseUrl}${
    baseUrl.includes("?") ? "&" : "?"
  }return_url=${getCalendarReturnUrl(
    client,
    opts
  )}&error_url=${getCalendarErrorUrl(client, opts)}`;

  if (client === "zoom") {
    url = `${url}&provider=zoom&provider_user_id=${getAppUserId()}`;
  }
  return url;
};

export const getCalendarModifyAuthUrl = ({
  client,
  calendarProvider,
  opts = {},
}: {
  client: string;
  calendarProvider: CalendarProviderId;
  opts?: ConnectCalendarOptions;
}) => {
  const baseUrl = CALENDAR_PROVIDERS_BY_ID[calendarProvider].connectUrl;
  let url = `${baseUrl}?return_url=${getCalendarReturnUrl(
    client,
    opts
  )}&error_url=${getCalendarErrorUrl(client, opts)}`;

  if (client === "zoom") {
    url = `${url}&provider=zoom&provider_user_id=${getAppUserId()}`;
  }
  return url;
};

const useConnectCalendar = () => {
  const { client } = useApps();

  const connectCalendar = async (
    calendarProvider: CalendarProviderId,
    opts?: ConnectCalendarOptions
  ) => {
    const calendarClient = opts?.client ?? client;

    const url = getCalendarConnectUrl({
      client: calendarClient,
      calendarProvider,
      opts,
    });
    Events.connectCalendar({ stage: "start", provider: calendarProvider });

    if (calendarClient === "zoom") {
      logger.debug("connectCalendar configureZoomSdk");
      await configureZoomSdk();
      await openAppBrowser(url);
      return;
    }

    window.location.href = url;
  };

  return { connectCalendar };
};

export default useConnectCalendar;
